/**
 * @docs docs/features/website-navigation-shell
 */

import React from 'react';

import useContext from '@/client/context';
import RoundLogo from '@/client/assets/identity/logo64.png';
// The folded mark at 2x: the phone shows `.hl-round` at 44px, and Lighthouse's image-size-responsive wants the natural
// size at the device ratio (66px at 1.5x), which the 64px mark missed on every website target (best practices 96;
// mock parity F2 item 3, 2026-11-05). The night mark at 25px keeps the 64px file. Cut from logo.svg's 512px image.
import RoundLogo2x from '@/client/assets/identity/logo88.png';
// The 480px cut, not the 1370px master: the slot is 190px wide at most, and the build serves
// the largest generated size as `src`, so the master shipped 1300px for every page view.
import SignatureLockup from '@/client/assets/identity/signature-lockup-ink-480.png';
// The phone night logo's wordmark, cut to twice its 127px slot (R5-W3 W3-8): the 1698px master is 44 KB, this cut 6 KB.
import WordmarkWhite from '@/client/assets/identity/wordmark-white-254.png';
import { Button } from '@uniquedomains/components/kit/button/Button';
import { IconButton } from '@uniquedomains/components/kit/icon-button/IconButton';
import Icon from '@/client/components/ui/icon/Icon';
import MetricsComponent from '@/client/services/metrics/Component';
import useProductSession from '@/client/hooks/useProductSession';
import {
    buildLandingHeaderNavItems,
    isLandingHeaderNavItemCurrent,
    type LandingHeaderDomainsNavigation,
    type LandingHeaderNavItem,
} from '@/client/catalogs/landing/pageContent';
import { track } from '@/client/pages/landing/lib/analytics';
import { trackConversionTrigger } from '@/client/pages/landing/lib/trackConversionTrigger';
import { trackLandingCta } from '@/client/pages/landing/lib/trackLandingCta';
import type { ConversionTriggerId } from '@uniquedomains/components/contracts/analytics/conversionTriggers';
import {
    isLandingDocumentScrollLocked,
    setLandingBodyClass,
    setLandingDocumentScrollLocked,
} from '@/client/pages/landing/lib/documentOverlayState';
import DomainsMegaMenu from './DomainsMegaMenu';
import MarketMegaMenu from './MarketMegaMenu';
import MobileSiteMenu from './MobileSiteMenu';
import type { LandingNavMenu, LandingNavSurface, MenuNavigateHandler } from './navClick';
import { resolveHeaderScrolledState } from './headerScrollState';
import ToolsMegaMenu from './ToolsMegaMenu';
import { resolveProductOrigin } from '@/client/services/conversion/product/productOrigin';
import { cachedLandingHeaderNavigation } from '@/common/libs/connected/publicProductReadCache';
import { HEADER_CTA_LABEL } from '@/client/catalogs/landing/accessOffer';
import type { Links } from '../../types';

const HEADER_MEGA_MENU_CLOSE_DELAY_MS = 400;
/*
 * Not "Start Free": the free plan is retired (ADR-0083); a signup opens the search, then the decision. The pill reads
 * "Start my search" since the frame moved onto the design system (batch 7, decision 2A). The phone has no bottom
 * action bar since Gaetan's decision of 2026-10-26 (applied 2026-10-27): a page's own action lives in its sections.
 */
const HEADER_PRIMARY_CTA_LABEL = HEADER_CTA_LABEL;
export const HEADER_OPEN_APP_LABEL = 'Open app';
/* The body class the marketing layer keys the compact pill on (`.scrolled .hdr` in marketing.css), as the mock's
   script does. */
const HEADER_SCROLLED_BODY_CLASS = 'scrolled';
/*
 * The pill floats over the page (`.hdr` is fixed). A page whose body was drawn for the in-flow header (every page until
 * its own rebuild) gets this spacer, the pill's resting height, so its first section is not covered. Constant on
 * purpose: the compact pill must never change the page's height, which is what made the in-flow header tremble under
 * scroll anchoring (headerScrollState.ts).
 */
const HEADER_SPACER_STYLE = { height: 'calc(var(--pill-top) + var(--pill-h))' };
const EMPTY_DOMAINS_NAVIGATION: LandingHeaderDomainsNavigation = {
    tlds: [],
    sectors: [],
};
/**
 * Login left the desktop bar on 2026-09-17. These ids did not: the phone menu's Log in
 * row and the footer's Log in link both fire the one that matches the page, told apart
 * in analytics by `section`. The trigger catalog is a cross-app contract, so a surface
 * moving is not a reason to mint a second set of ids.
 */
export const HEADER_LOGIN_TRIGGER_BY_PAGE: Record<string, ConversionTriggerId> = {
    api_docs: 'website_api_header_login',
    domains_public: 'website_domains_header_login',
    landing_home: 'website_landing_header_login',
    pricing: 'website_pricing_header_login',
    product_appraise: 'website_product_appraise_header_login',
    product_radars: 'website_product_radars_header_login',
    product_search: 'website_product_search_header_login',
    product_verify: 'website_product_verify_header_login',
    product_watchlists: 'website_product_watchlists_header_login',
    solution_founders: 'website_solution_founders_header_login',
    solution_investors: 'website_solution_investors_header_login',
    team: 'website_team_header_login',
};
/**
 * The primary CTA's ids per page. The footer's Access "Start with my search" (E17 1C) fires the same id, told apart by
 * `section: 'footer_access'`, as the footer's Log in does with the login ids.
 */
export const HEADER_PRIMARY_TRIGGER_BY_PAGE: Record<string, ConversionTriggerId> = {
    api_docs: 'website_api_header_try_free',
    domains_public: 'website_domains_header_try_free',
    landing_home: 'website_landing_header_try_free',
    pricing: 'website_pricing_header_try_free',
    product_appraise: 'website_product_appraise_header_try_free',
    product_radars: 'website_product_radars_header_try_free',
    product_search: 'website_product_search_header_try_free',
    product_verify: 'website_product_verify_header_try_free',
    product_watchlists: 'website_product_watchlists_header_try_free',
    solution_founders: 'website_solution_founders_header_try_free',
    solution_investors: 'website_solution_investors_header_try_free',
    team: 'website_team_header_try_free',
};

type HeaderMenuId = 'domains' | 'market' | 'tools';
type HeaderMegaNavItem = Extract<LandingHeaderNavItem, { kind: 'mega' }>;
type HeaderProps = {
    links: Links;
    navigation?: LandingHeaderDomainsNavigation;
    brandHref?: string;
    currentPath?: string;
    trackingPage: string;
    /**
     * Reserves the pill's height above the page (default). A page rebuilt on the marketing layer draws its first section
     * under the floating pill itself (Home's hero card starts `--card-top` from the top) and passes `false`.
     */
    spacer?: boolean;
};

const HEADER_MEGA_MENU_IDS: Record<HeaderMenuId, string> = {
    domains: 'landing-header-domains-menu',
    market: 'landing-header-market-menu',
    tools: 'landing-header-tools-menu',
};
const getHeaderMenuId = (menu: HeaderMenuId): string => HEADER_MEGA_MENU_IDS[menu];
/** The phone drawer's id, which the menu button controls. */
const MOBILE_SITE_MENU_ID = 'landing-mobile-site-menu';
/** Where the pill grows its navigation (marketing.css, `min-width: 1024px`): the phone drawer has no place there. */
const DESKTOP_HEADER_MEDIA_QUERY = '(min-width: 1024px)';

export const resolveHeaderTriggerId = (
    trackingPage: string,
    map: Record<string, ConversionTriggerId>,
    fallback: ConversionTriggerId,
): ConversionTriggerId => map[trackingPage] || fallback;

const scrollWindowToTop = () => {
    if (typeof window === 'undefined') return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({
        top: 0,
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
};

export function Header({
    links,
    navigation = EMPTY_DOMAINS_NAVIGATION,
    brandHref = '/',
    currentPath = '',
    trackingPage,
    spacer = true,
}: HeaderProps) {
    // Every landing page funnels its resolved navigation payload through this
    // header, so this is the one seam that seeds the public read cache; page
    // `data` fetchers then reuse it instead of re-asking Product per render.
    // The empty fallback is a render placeholder, never a payload to cache.
    if (navigation !== EMPTY_DOMAINS_NAVIGATION) cachedLandingHeaderNavigation.seed(navigation);
    const { enforceDomainPublicationLinks, publicOrigins, renderedDomainResourcePaths } = useContext();
    /*
     * The one place the public site learns whether the visitor is already a member.
     * SSR cannot: these pages are rendered once and replayed to everyone, and the Product
     * session cookie is host-only on the Product host, so it never reaches this origin.
     * The header renders on every landing page, so probing here covers the whole site for
     * one request per document, shared with the metrics component and the /domains page.
     * See docs/fixes/2026-09-14-website-header-blind-to-members.md.
     */
    const { status: productSessionStatus } = useProductSession(publicOrigins.productOrigin);
    const isMember = productSessionStatus === 'member';
    const productHomeHref = `${resolveProductOrigin(publicOrigins.productOrigin)}/`;
    const headerRef = React.useRef<HTMLElement | null>(null);
    const headerNavRef = React.useRef<HTMLDivElement | null>(null);
    const headerMenuCloseTimeoutRef = React.useRef<number | null>(null);
    const pendingPointerMenuIdRef = React.useRef<string | null>(null);
    const suppressHeaderFocusOpenRef = React.useRef(false);

    const [activeHeaderMenuId, setActiveHeaderMenuId] = React.useState<string | null>(null);
    const [isHeaderScrolled, setIsHeaderScrolled] = React.useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

    const publishedDomainPaths = enforceDomainPublicationLinks
        ? renderedDomainResourcePaths
        : undefined;
    const headerNavItems = React.useMemo(
        () => buildLandingHeaderNavItems(navigation, publishedDomainPaths),
        [navigation, publishedDomainPaths],
    );

    const activeHeaderMenu = React.useMemo(() => {
        const menu = headerNavItems.find((item) => item.id === activeHeaderMenuId);
        if (!menu || menu.kind !== 'mega') return null;
        return menu;
    }, [activeHeaderMenuId, headerNavItems]);

    const domainsMenu = React.useMemo(() => {
        const menu = headerNavItems.find((item) => item.id === 'domains');
        if (!menu || menu.kind !== 'mega' || menu.menu !== 'domains') return null;
        return menu;
    }, [headerNavItems]);

    const cancelHeaderMenuClose = React.useCallback(() => {
        if (headerMenuCloseTimeoutRef.current === null) return;

        window.clearTimeout(headerMenuCloseTimeoutRef.current);
        headerMenuCloseTimeoutRef.current = null;
    }, []);

    const closeMobileMenu = React.useCallback(() => setIsMobileMenuOpen(false), []);

    const scheduleHeaderMenuClose = React.useCallback(() => {
        cancelHeaderMenuClose();

        headerMenuCloseTimeoutRef.current = window.setTimeout(() => {
            setActiveHeaderMenuId(null);
            headerMenuCloseTimeoutRef.current = null;
        }, HEADER_MEGA_MENU_CLOSE_DELAY_MS);
    }, [cancelHeaderMenuClose]);

    const handleHeaderNavEnter = React.useCallback(
        (item: LandingHeaderNavItem) => {
            cancelHeaderMenuClose();

            if (item.kind !== 'mega') {
                setActiveHeaderMenuId(null);
                return;
            }

            setActiveHeaderMenuId(item.id);
        },
        [cancelHeaderMenuClose],
    );

    const handleHeaderNavFocus = React.useCallback(
        (item: HeaderMegaNavItem) => {
            if (suppressHeaderFocusOpenRef.current) {
                suppressHeaderFocusOpenRef.current = false;
                return;
            }

            handleHeaderNavEnter(item);
        },
        [handleHeaderNavEnter],
    );

    const focusFirstHeaderMenuItem = React.useCallback(
        (item: HeaderMegaNavItem) => {
            handleHeaderNavEnter(item);

            window.requestAnimationFrame(() => {
                document
                    .getElementById(getHeaderMenuId(item.menu))
                    ?.querySelector<HTMLElement>('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
                    ?.focus();
            });
        },
        [handleHeaderNavEnter],
    );

    const handleHeaderMegaNavKeyDown = React.useCallback(
        (event: React.KeyboardEvent<HTMLElement>, item: HeaderMegaNavItem) => {
            if (event.key !== 'ArrowDown') return;

            event.preventDefault();
            focusFirstHeaderMenuItem(item);
        },
        [focusFirstHeaderMenuItem],
    );

    const handleHeaderMegaNavPointerDown = React.useCallback(
        (event: React.PointerEvent<HTMLElement>, item: HeaderMegaNavItem) => {
            pendingPointerMenuIdRef.current =
                event.pointerType !== 'mouse' && activeHeaderMenuId !== item.id ? item.id : null;
        },
        [activeHeaderMenuId],
    );

    const closeHeaderMenu = React.useCallback(() => {
        cancelHeaderMenuClose();
        setActiveHeaderMenuId(null);
    }, [cancelHeaderMenuClose]);

    /*
     * One event for every followed link in the bar, the three panels and the phone menu,
     * so a menu's use can be read without joining per-surface ids. It is not a conversion
     * trigger: the trigger catalog is a cross-app contract of conversion-path clicks, and a
     * menu link is not one. CTAs that open the product keep `landing_cta_click` and their
     * trigger ids; this event records only where the visitor went next.
     */
    const trackNavClick = React.useCallback(
        (menu: LandingNavMenu, surface: LandingNavSurface, item: string, href: string) => {
            track('landing_nav_click', { page: trackingPage, menu, item, href, surface });
        },
        [trackingPage],
    );

    const navigateFromMegaMenu = React.useCallback(
        (menu: HeaderMenuId): MenuNavigateHandler => (item, href) => trackNavClick(menu, 'megamenu', item, href),
        [trackNavClick],
    );

    const navigateFromMobileMenu = React.useCallback<MenuNavigateHandler>(
        (item, href) => {
            trackNavClick('mobile', 'mobile_menu', item, href);
            closeMobileMenu();
        },
        [closeMobileMenu, trackNavClick],
    );

    const handleHeaderMegaNavClick = React.useCallback(
        (event: React.MouseEvent<HTMLAnchorElement>, item: HeaderMegaNavItem) => {
            if (pendingPointerMenuIdRef.current === item.id) {
                pendingPointerMenuIdRef.current = null;
                event.preventDefault();
                handleHeaderNavEnter(item);
                return;
            }

            // Only a click that leaves for the hub page is navigation. The touch tap above
            // opens the panel instead, and counting it would inflate Domains with menu opens.
            if (item.href) trackNavClick('bar', 'header', item.id, item.href);
            closeHeaderMenu();
        },
        [closeHeaderMenu, handleHeaderNavEnter, trackNavClick],
    );

    /*
     * Tools has no hub page, so its trigger is a disclosure button rather than an
     * anchor: there is no URL to send a crawler or a keyboard user to, and `href="#"`
     * would be both. A click toggles the panel it controls, except on the tap that
     * opened it: a touch tap emits an emulated `mouseenter` before its `click`, so the
     * open and the toggle would land in the same gesture and the panel would flash shut.
     * The pointer-intent ref is the same one the anchor triggers use.
     */
    const handleHeaderMegaNavToggle = React.useCallback(
        (item: HeaderMegaNavItem) => {
            if (pendingPointerMenuIdRef.current === item.id) {
                pendingPointerMenuIdRef.current = null;
                handleHeaderNavEnter(item);
                return;
            }

            if (activeHeaderMenuId === item.id) {
                closeHeaderMenu();
                return;
            }

            handleHeaderNavEnter(item);
        },
        [activeHeaderMenuId, closeHeaderMenu, handleHeaderNavEnter],
    );

    // Where a header-owned click came from: the phone sheet or the pill. The per-page trigger ids are shared by
    // both (the trigger catalog is a cross-app contract), so this section value tells them apart.
    const resolveCtaSection = React.useCallback(() => (isMobileMenuOpen ? 'header_mobile_menu' : 'header'), [isMobileMenuOpen]);

    const triggerPrimaryCta = React.useCallback(() => {
        const section = resolveCtaSection();

        trackLandingCta({
            page: trackingPage,
            section,
            label: HEADER_PRIMARY_CTA_LABEL,
            kind: 'product',
            href: links.productBaseUrl,
            surface: 'header',
        });
        trackConversionTrigger(
            resolveHeaderTriggerId(trackingPage, HEADER_PRIMARY_TRIGGER_BY_PAGE, 'website_landing_header_try_free'),
            {
                href: links.productBaseUrl,
                page: trackingPage,
                section,
            },
        );

        track('cta_open_screener_click', {
            section,
            label: HEADER_PRIMARY_CTA_LABEL.toLowerCase(),
        });
    }, [links.productBaseUrl, resolveCtaSection, trackingPage]);

    /*
     * A member's click is navigation, not conversion: no trigger id is emitted. The
     * conversion-trigger maps above are per-page catalogs of signup and login intents,
     * pinned by product-search-page-contract.test.cjs, and there is no entry for a member
     * who is already past the funnel. `landing_cta_click` still records the click.
     */
    const triggerOpenAppCta = React.useCallback(() => {
        trackLandingCta({
            page: trackingPage,
            section: resolveCtaSection(),
            label: HEADER_OPEN_APP_LABEL,
            kind: 'product',
            href: productHomeHref,
            surface: 'header',
        });

        closeMobileMenu();
    }, [closeMobileMenu, productHomeHref, resolveCtaSection, trackingPage]);

    const triggerLoginCta = React.useCallback(() => {
        const section = resolveCtaSection();

        trackLandingCta({
            page: trackingPage,
            section,
            label: 'Login',
            kind: 'auth',
            href: links.signIn,
            // Login is a phone-only control in the shell since 2026-09-17, and the phone menu is its one place
            // since the action bar was dropped (2026-10-27).
            surface: 'mobile_menu',
        });
        trackConversionTrigger(
            resolveHeaderTriggerId(trackingPage, HEADER_LOGIN_TRIGGER_BY_PAGE, 'website_landing_header_login'),
            {
                href: links.signIn,
                page: trackingPage,
                section,
            },
        );

        closeMobileMenu();
    }, [closeMobileMenu, links.signIn, resolveCtaSection, trackingPage]);

    const trackGithubNavOpen = React.useCallback(
        (section: string) => {
            trackLandingCta({
                page: trackingPage,
                section,
                label: 'GitHub continuity',
                kind: 'github',
                href: links.githubLists,
                surface: 'header',
            });
            track('github_repo_open_attempt', {
                section,
                target: 'github_lists',
            });
        },
        [links.githubLists, trackingPage],
    );

    const handleBrandClick = React.useCallback(
        (event: React.MouseEvent<HTMLAnchorElement>) => {
            if (typeof window === 'undefined') return;
            // The logo stays over the open phone drawer: following it to another page closes the drawer, as the mock does.
            if (window.location.pathname !== brandHref) {
                closeMobileMenu();
                return;
            }

            event.preventDefault();
            setActiveHeaderMenuId(null);

            if (isMobileMenuOpen) {
                closeMobileMenu();
                window.setTimeout(() => scrollWindowToTop(), 20);
                return;
            }

            scrollWindowToTop();
        },
        [brandHref, closeMobileMenu, isMobileMenuOpen],
    );

    React.useEffect(() => {
        const headerElement = headerRef.current;
        if (!headerElement) return;

        const updateTopbarHeight = () => {
            const height = headerElement.getBoundingClientRect().height;
            const topOffset = Number.parseFloat(window.getComputedStyle(headerElement).top || '0') || 0;
            document.documentElement.style.setProperty('--ud-topbar-h', `${Math.ceil(height + topOffset)}px`);
        };

        updateTopbarHeight();

        if (typeof ResizeObserver === 'undefined') return;

        const observer = new ResizeObserver(() => updateTopbarHeight());
        observer.observe(headerElement);

        return () => observer.disconnect();
    }, []);

    React.useEffect(() => {
        if (typeof window === 'undefined') return;

        const updateScrolledState = () => {
            // While the drawer (or any overlay) pins the body, `scrollY` reads 0: folding the logo back then would flash
            // the night hero's white logo over the page as the drawer closes (R5-W3 W3-6). The restore's own scroll event
            // brings the state back once the lock is released.
            if (isLandingDocumentScrollLocked()) return;
            setIsHeaderScrolled((wasScrolled) => resolveHeaderScrolledState(wasScrolled, window.scrollY));
        };

        updateScrolledState();
        window.addEventListener('scroll', updateScrolledState, { passive: true });

        return () => window.removeEventListener('scroll', updateScrolledState);
    }, [currentPath]);

    // The compact pill is drawn by `.scrolled .hdr` (marketing.css): the state lands on the body, as the mock's script
    // puts it, and leaves with the header.
    React.useEffect(() => {
        setLandingBodyClass(HEADER_SCROLLED_BODY_CLASS, isHeaderScrolled);
        return () => setLandingBodyClass(HEADER_SCROLLED_BODY_CLASS, false);
    }, [isHeaderScrolled]);

    React.useEffect(() => {
        if (!activeHeaderMenuId) return;

        const handlePointerDown = (event: MouseEvent) => {
            const target = event.target;
            if (!(target instanceof Node)) return;
            if (headerNavRef.current?.contains(target)) return;
            setActiveHeaderMenuId(null);
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return;

            const activeTrigger = headerNavRef.current?.querySelector<HTMLElement>(
                `[data-testid="header-${activeHeaderMenuId}-link"]`,
            );

            // Focus first, close second. Closing schedules a render that unmounts the panel,
            // and Preact can run it before this handler reaches the focus call, which leaves
            // focus on a removed node and strands keyboard users at the top of the document.
            // Moving the focus ahead of the state change makes it land while the trigger is
            // certainly still mounted. The suppression ref keeps that focus from reopening
            // the menu it is closing.
            if (activeTrigger && document.activeElement !== activeTrigger) {
                suppressHeaderFocusOpenRef.current = true;
                activeTrigger.focus();
            }

            setActiveHeaderMenuId(null);
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [activeHeaderMenuId]);

    // The open drawer locks the page's scroll. Its body class (`ud-landing-header-menu-open`) went with the phone action
    // bar, its only reader (batch 7 review m-A5).
    React.useEffect(() => {
        const scrollLockId = 'landing-header-menu';
        setLandingDocumentScrollLocked(scrollLockId, isMobileMenuOpen);
        return () => setLandingDocumentScrollLocked(scrollLockId, false);
    }, [isMobileMenuOpen]);

    /*
     * A drawer left open while the window widens past 1024px (a rotated tablet, a resized window) would stay over the
     * desktop pill and keep the body scroll lock. The drawer has no desktop look, so reaching the desktop
     * breakpoint closes it, which also releases the lock. Found in the browser check of batch 7 C3.
     */
    React.useEffect(() => {
        if (!isMobileMenuOpen) return;

        const desktopQuery = window.matchMedia(DESKTOP_HEADER_MEDIA_QUERY);
        const closeOnDesktop = () => {
            if (desktopQuery.matches) closeMobileMenu();
        };

        closeOnDesktop();
        desktopQuery.addEventListener('change', closeOnDesktop);
        return () => desktopQuery.removeEventListener('change', closeOnDesktop);
    }, [closeMobileMenu, isMobileMenuOpen]);

    React.useEffect(() => {
        if (!isMobileMenuOpen) return;
        // The focus goes back to the menu button, found in the header it belongs to. When the drawer closed
        // because the window widened past 1024px, the trigger is hidden by the desktop sheet and cannot take the focus
        // (the band review, batch 6 S6): the focus goes to the brand link, the header's first control at every width.
        return () => {
            const header = headerRef.current;
            const trigger = header?.querySelector<HTMLElement>('.hl-menu');
            const target = trigger && trigger.getClientRects().length > 0 ? trigger : header?.querySelector<HTMLElement>('.hl-brand');
            target?.focus();
        };
    }, [isMobileMenuOpen]);

    React.useEffect(() => {
        return () => {
            if (headerMenuCloseTimeoutRef.current === null) return;
            window.clearTimeout(headerMenuCloseTimeoutRef.current);
        };
    }, []);

    return (
        <>
            <header ref={headerRef} class="hdr">
                <MetricsComponent />
                {/*
                  * The marketing layer's pill (`.hdr > .hdr-bar`), white at all times. From 1024px it holds the
                  * lockup, the navigation and the call to action, and compacts past 48px of scroll (the lockup folds
                  * into the round mark). Below 1024px there is no pill (Home v3, 2026-11-04): the lockup and the menu
                  * button stand alone over the page, the lockup folding into the round mark the same way.
                  */}
                <div class="hdr-bar">
                    <a href={brandHref} onClick={handleBrandClick} class="hl-brand" aria-label="Unique Domains home">
                        <img class="hl-lockup" src={SignatureLockup} alt="" width="190" height="36" />
                        <img class="hl-round" src={RoundLogo2x} alt="" width="36" height="36" />
                        {/* The white logo over Home's night hero (`body.hero-night`, phone only, marketing.css): the
                            round mark and the white wordmark where the ink lockup's circle and words sit. Shown only
                            there, so lazy: a page that never shows them never fetches the wordmark. */}
                        <img class="hl-night hl-night-mark" src={RoundLogo} alt="" width="25" height="25" loading="lazy" decoding="async" />
                        <img class="hl-night hl-night-word" src={WordmarkWhite} alt="" width="127" height="19" loading="lazy" decoding="async" />
                    </a>

                    <div class="hl-navwrap">
                        <div
                            ref={headerNavRef}
                            class="hl-navbox"
                            onMouseEnter={cancelHeaderMenuClose}
                            onMouseLeave={scheduleHeaderMenuClose}
                        >
                            <nav aria-label="Primary" class="hl-nav">
                                {headerNavItems.map((item) => {
                                    const isActive = activeHeaderMenuId === item.id;
                                    const isCurrentItem = isLandingHeaderNavItemCurrent(item, currentPath);

                                    if (item.kind === 'static') {
                                        // Captured in a const so the click closure below keeps
                                        // the narrowed string type instead of `item.href`'s optional one.
                                        const staticHref = item.href;
                                        if (staticHref) {
                                            return (
                                                <a
                                                    key={item.id}
                                                    href={staticHref}
                                                    onMouseEnter={() => handleHeaderNavEnter(item)}
                                                    onFocus={() => setActiveHeaderMenuId(null)}
                                                    onClick={() => {
                                                        trackNavClick('bar', 'header', item.id, staticHref);
                                                        closeHeaderMenu();
                                                    }}
                                                    aria-current={currentPath === staticHref ? 'page' : undefined}
                                                    class={isCurrentItem ? 'hl-item hl-static is-current' : 'hl-item hl-static'}
                                                >
                                                    {item.label}
                                                </a>
                                            );
                                        }

                                        return (
                                            <span key={item.id} class="hl-item hl-static">
                                                {item.label}
                                            </span>
                                        );
                                    }

                                    return (
                                        <div
                                            key={item.id}
                                            data-menu={item.menu}
                                            class={[
                                                'hl-grp',
                                                isActive ? 'on' : '',
                                                isCurrentItem ? 'is-current' : '',
                                            ].filter(Boolean).join(' ')}
                                        >
                                            {item.href ? (
                                                <a
                                                    href={item.href}
                                                    class="hl-item"
                                                    onMouseEnter={() => handleHeaderNavEnter(item)}
                                                    onFocus={() => handleHeaderNavFocus(item)}
                                                    onKeyDown={(event) => handleHeaderMegaNavKeyDown(event, item)}
                                                    onPointerDown={(event) =>
                                                        handleHeaderMegaNavPointerDown(event, item)}
                                                    onPointerCancel={() => {
                                                        pendingPointerMenuIdRef.current = null;
                                                    }}
                                                    onClick={(event) => handleHeaderMegaNavClick(event, item)}
                                                    aria-expanded={isActive}
                                                    aria-controls={getHeaderMenuId(item.menu)}
                                                    data-testid={`header-${item.id}-link`}
                                                >
                                                    {item.label}
                                                </a>
                                            ) : (
                                                <button
                                                    type="button"
                                                    class="hl-item"
                                                    onMouseEnter={() => handleHeaderNavEnter(item)}
                                                    onFocus={() => handleHeaderNavFocus(item)}
                                                    onKeyDown={(event) => handleHeaderMegaNavKeyDown(event, item)}
                                                    onPointerDown={(event) =>
                                                        handleHeaderMegaNavPointerDown(event, item)}
                                                    onPointerCancel={() => {
                                                        pendingPointerMenuIdRef.current = null;
                                                    }}
                                                    onClick={() => handleHeaderMegaNavToggle(item)}
                                                    aria-expanded={isActive}
                                                    aria-controls={getHeaderMenuId(item.menu)}
                                                    data-testid={`header-${item.id}-link`}
                                                >
                                                    {item.label}
                                                </button>
                                            )}

                                            {/* Tools opens centred on its own trigger (`.hl-grp` is the panel's positioned
                                                parent). Market's group is `position: static` (`[data-menu="market"]`,
                                                marketing.css), so its panel opens centred on the whole nav in Domains'
                                                place and frame: moving between the two keeps the frame still
                                                (web-recheck-05). */}
                                            {item.menu === 'market' ? (
                                                <MarketMegaMenu
                                                    id={getHeaderMenuId('market')}
                                                    isOpen={activeHeaderMenu?.menu === 'market'}
                                                    onNavigate={navigateFromMegaMenu('market')}
                                                />
                                            ) : null}

                                            {item.menu === 'tools' ? (
                                                <ToolsMegaMenu
                                                    id={getHeaderMenuId('tools')}
                                                    isOpen={activeHeaderMenu?.menu === 'tools'}
                                                    onNavigate={navigateFromMegaMenu('tools')}
                                                />
                                            ) : null}
                                        </div>
                                    );
                                })}
                            </nav>

                            {domainsMenu ? (
                                <DomainsMegaMenu
                                    id={getHeaderMenuId('domains')}
                                    isOpen={activeHeaderMenu?.menu === 'domains'}
                                    menu={domainsMenu}
                                    screenerHref={links.screener}
                                    onNavigate={navigateFromMegaMenu('domains')}
                                />
                            ) : null}
                        </div>
                    </div>

                    {/* One call to action, swapped in place for a member: the pill's geometry never changes. */}
                    <div class="hl-right">
                        <Button
                            variant="ink"
                            size="md"
                            cta
                            href={isMember ? productHomeHref : links.productBaseUrl}
                            onClick={() => (isMember ? triggerOpenAppCta() : triggerPrimaryCta())}
                            data-testid="header-primary-cta"
                        >
                            {isMember ? HEADER_OPEN_APP_LABEL : HEADER_PRIMARY_CTA_LABEL}
                        </Button>
                    </div>

                    {/* The phone's 44px menu button opens and closes the drawer: it stays over the open drawer, ink, its
                        menu glyph turned into the close glyph (both drawn, crossfaded in marketing.css). */}
                    <IconButton
                        class="hl-menu"
                        size="xl"
                        variant={isMobileMenuOpen ? 'ink' : 'default'}
                        onClick={() => setIsMobileMenuOpen((isOpen) => !isOpen)}
                        aria-expanded={isMobileMenuOpen}
                        aria-controls={MOBILE_SITE_MENU_ID}
                        label={isMobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
                        data-testid="header-menu-button"
                        icon={
                            <>
                                <Icon name="menu" class="hl-ic-menu" size={18} strokeWidth={2} />
                                <Icon name="x" class="hl-ic-x" size={18} strokeWidth={2} />
                            </>
                        }
                    />
                </div>
            </header>

            {/* The page under an open menu blurs (desktop only, `.hl-backdrop`); it never takes a click. */}
            <div class="hl-backdrop" aria-hidden="true" hidden={!activeHeaderMenu} />
            {spacer ? <div aria-hidden="true" style={HEADER_SPACER_STYLE} /> : null}

            <MobileSiteMenu
                id={MOBILE_SITE_MENU_ID}
                isOpen={isMobileMenuOpen}
                links={links}
                domainsMenu={domainsMenu}
                headerRef={headerRef}
                isMember={isMember}
                openApp={isMember ? { href: productHomeHref, label: HEADER_OPEN_APP_LABEL, onClick: triggerOpenAppCta } : null}
                onClose={closeMobileMenu}
                onNavigate={navigateFromMobileMenu}
                onLogin={() => triggerLoginCta()}
                onGithub={() => {
                    trackGithubNavOpen('header_mobile_menu');
                    closeMobileMenu();
                }}
            />
        </>
    );
}
